import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DemoSeedResult } from '@signalvote/shared';
import { Database, Loader2, RefreshCw } from 'lucide-react';
import { useCampaignStore } from '@/stores';
import { authService } from '@/services/authService';
import { API_BASE_URL } from '@/config';
import { financeQueries, queryKeys } from '@/lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * Demo-only "North Carolina demo dataset" control (ADR-056, beat 1). Gated purely on the
 * selected campaign's `isDemo` flag — the backend endpoint 403s any non-demo campaign as
 * defense-in-depth. Seeding always clears first, so "Seed NC demo" and "Reset & reseed" both
 * land on the same pristine state. The seeded transactions arrive UNCONFIRMED, so the rep
 * reviews and confirms them, then generates the filing, through the normal finance workflow
 * (no demo shortcut) — the demo shows the real product end to end.
 */
export function DemoSeedCard() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { mutate: seed, isPending: seeding } = useMutation({
    mutationFn: async () => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/dev/seed`,
        { method: 'POST' },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Seed failed' }));
        throw new Error(err.error ?? 'Seed failed');
      }
      const seeded: DemoSeedResult = await response.json();
      return seeded;
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries(financeQueries);
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!selectedCampaign?.isDemo) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="h-4 w-4" />
          North Carolina demo dataset
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Populate this demo campaign with a realistic North Carolina committee — within-limit
          donors, a PAC, a party executive committee, a candidate self-fund, an in-kind gift, and
          expenditures. Transactions land <strong>unconfirmed</strong>, so you review and confirm
          them, then generate the current-period report — all through the normal finance workflow.
          Seeding always resets first, so it's safe to re-run.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => seed()} disabled={seeding}>
            {seeding ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Seeding…
              </>
            ) : (
              <>
                <Database className="mr-2 h-3.5 w-3.5" />
                Seed NC demo
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => seed()} disabled={seeding}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Reset &amp; reseed
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </CardContent>
    </Card>
  );
}
